import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VmeCommonModule } from '../../../vme-common.module';
import { TravelSchedulerTask } from '../../../interfaces/travel-scheduler/travel-scheduler-task';
import { VmePipesModule } from '../../../vme-pipes.module';
import { ScheduleDropdownComponent } from '../schedule-dropdown/schedule-dropdown.component';
import { VMEComponentsModule } from '../../../vme-components.module';
import { TravelSchedulerService } from '../../../services/travel-scheduler.service';
import { TimeWindowResponse } from '../../../interfaces/travel-scheduler/time-window-response';
import { TaskSchedule } from '../../../interfaces/travel-scheduler/task-schedule';
import { VerticalDotsContainerComponent } from '../../../modules/driver-experience/job-list/vertical-dots-container.component';

@Component({
  selector: 'schedule-selector',
  standalone: true,
  imports: [
    CommonModule,
    VmeCommonModule,
    VmePipesModule,
    ScheduleDropdownComponent,
    VerticalDotsContainerComponent,
  ],
  templateUrl: './schedule-selector.component.html',
  styleUrls: ['./schedule-selector.component.css'],
})
export class ScheduleSelectorComponent implements OnInit, OnChanges {
  constructor(private travelSchedulerService: TravelSchedulerService) {}

  isExistingPickupOnPreviousDay: boolean = false;
  isExistingDropoffOnPreviousDay: boolean = false;

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['task']) {
      await this.loadPickupWindows();
      this.canCarryover = this.task.isSupplierCarryingover ?? false;
      if (this.task.supplierPickupWindow != null) {
        await this.getAvailableDropoffWindows();
      }
    }
  }

  protected pickupTimeWindows: TimeWindowResponse;
  protected dropoffTimeWindows: TimeWindowResponse;

  protected canCarryover: boolean = false;

  async ngOnInit(): Promise<void> {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    this.isExistingPickupOnPreviousDay =
      this.task.supplierPickupWindow != null && this.task.supplierPickupWindow < startOfToday;

    this.isExistingDropoffOnPreviousDay =
      this.task.supplierDropoffWindow != null && this.task.supplierDropoffWindow < startOfToday;
  }

  protected async loadPickupWindows() {
    this.pickupTimeWindows = await this.travelSchedulerService.getAvailablePickupWindows(
      this.task.id,
      this.previousTaskDropoffWindow,
    );
  }

  protected handleCarryoverToggle(value: boolean) {
    this.canCarryover = value;
    if (this.task.supplierPickupWindow) {
      void this.getAvailableDropoffWindows();
    }
  }

  @Output() scheduleSelected = new EventEmitter<TaskSchedule>();
  @Input() task!: TravelSchedulerTask;

  @Input() previousTaskDropoffWindow: Date;

  handlePickupScheduleSelected(schedule: Date) {
    this.task.supplierPickupWindow = schedule;

    if (!schedule == null) {
      return;
    }

    this.getAvailableDropoffWindows().then(() => {
      // if every dropoff date is not the selected pickup date, it must be a carryover, so switch toggle on.
      if (
        this.dropoffTimeWindows.dateOptions.every((x) => x.date.getDate() != schedule.getDate())
      ) {
        this.canCarryover = true;
      }
    });

    // if the pickup is now later than the dropoff, reset the dropoff
    if (new Date(this.task.supplierPickupWindow) > new Date(this.task.supplierDropoffWindow)) {
      this.task.supplierDropoffWindow = null;
    }
  }

  private async getAvailableDropoffWindows() {
    this.dropoffTimeWindows = await this.travelSchedulerService.getAvailableDropoffWindows(
      this.task.id,
      this.task.supplierPickupWindow,
      this.canCarryover,
    );
  }

  private selectedSchedule: TaskSchedule;
  protected showPickupCalendar: boolean = false;
  protected showDropoffCalendar: boolean = false;
  @Input() isVME: boolean = false;
  @Input() isAlreadyScheduledForToday: boolean = false;

  handleDropoffScheduleSelected(schedule: Date) {
    this.task.supplierDropoffWindow = schedule;
    if (schedule != null) {
      this.selectedSchedule = {
        selectedPickupWindow: this.task.supplierPickupWindow,
        selectedDropoffWindow: this.task.supplierDropoffWindow,
        isCarryover: this.canCarryover,
      };
      this.scheduleSelected.emit(this.selectedSchedule);
    }
  }

  isDropoffScheduleDisabled() {
    if (this.task.supplierPickupWindow == null) {
      return true;
    }
    return false;
  }

  pickupCalendarToggled($event: boolean) {
    this.showPickupCalendar = $event;
    // do not show both calendars at once. If one opens, close the other.
    if ($event) {
      this.showDropoffCalendar = false;
    }
  }

  dropoffCalendarToggled($event: boolean) {
    this.showDropoffCalendar = $event;
    if ($event) {
      this.showPickupCalendar = false;
    }
  }
}
